import { Sentry } from '@/sentry';

interface ErrorReport {
    message: string;
    stack?: string;
    errorId: string;
    timestamp: string;
    url: string;
    userAgent: string;
    userId?: string;
}

class GlobalErrorHandler {
    private static instance: GlobalErrorHandler;
    private errorQueue: ErrorReport[] = [];
    private isReporting = false;
    // Guards against the handler re-entering itself. Reporting an error can
    // synchronously surface another error (e.g. a stack overflow), which would
    // otherwise re-fire the global listeners and loop until the call stack blows.
    private isHandling = false;

    private constructor() {
        this.setupGlobalHandlers();
    }

    static getInstance(): GlobalErrorHandler {
        if (!GlobalErrorHandler.instance) {
            GlobalErrorHandler.instance = new GlobalErrorHandler();
        }
        return GlobalErrorHandler.instance;
    }

    private setupGlobalHandlers() {
        // Handle unhandled promise rejections
        window.addEventListener('unhandledrejection', event => {
            const reason = event.reason;
            // Preserve the original Error (and its stack) when available; only
            // synthesize one for non-Error rejections.
            const error = reason instanceof Error ? reason : new Error(String(reason));
            this.handleError(error, 'unhandledrejection');
        });

        // Handle uncaught errors. NOTE: a single listener only — a second
        // 'error' listener double-reports every error, and re-wrapping
        // event.error into a new Error() destroys the original stack and can
        // feed a reporting loop (see QUANTIX-API-4Z).
        window.addEventListener('error', event => {
            const error = event.error instanceof Error ? event.error : new Error(event.message);
            const source =
                event.error?.name === 'React Error Boundary' ? 'react-boundary' : 'uncaught';
            this.handleError(error, source);
        });
    }

    handleError(error: Error, source: string = 'manual') {
        // Bail out if we're already handling an error. Without this guard, an
        // error thrown while reporting (or a stack-overflow that keeps firing
        // the global 'error' listener) recurses until the stack is exhausted.
        if (this.isHandling) {
            return '';
        }
        this.isHandling = true;

        try {
            const errorReport: ErrorReport = {
                message: error.message,
                stack: error.stack,
                errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                timestamp: new Date().toISOString(),
                url: window.location.href,
                userAgent: navigator.userAgent,
                userId: this.getCurrentUserId(),
            };

            // Log to console in development
            if (process.env.NODE_ENV === 'development') {
                console.error(`[${source}] Error caught by GlobalErrorHandler:`, errorReport);
            }

            // Report to Sentry (same project as quantix-api)
            Sentry.captureException(error, {
                tags: { source },
                extra: { errorId: errorReport.errorId, url: errorReport.url },
                user: errorReport.userId ? { id: errorReport.userId } : undefined,
            });

            // Add to queue for reporting
            this.errorQueue.push(errorReport);

            // Report errors in batches
            this.reportErrors();

            return errorReport.errorId;
        } finally {
            this.isHandling = false;
        }
    }

    private getCurrentUserId(): string | undefined {
        // Try to get user ID from localStorage, session, or auth context
        try {
            const authData = localStorage.getItem('auth_data');
            if (authData) {
                const parsed = JSON.parse(authData);
                return parsed.user?.id || parsed.customer?.id;
            }
        } catch (e) {
            // Ignore errors getting user ID
        }
        return undefined;
    }

    private async reportErrors() {
        if (this.isReporting || this.errorQueue.length === 0) {
            return;
        }

        this.isReporting = true;

        const errorsToReport = [...this.errorQueue];
        this.errorQueue = [];

        try {
            // Report to your error reporting service
            await this.sendErrorReport(errorsToReport);
        } catch (e) {
            // If reporting fails, put the failed batch back on the queue so it
            // isn't lost. (Previously this spread the already-emptied queue into
            // itself, which restored nothing.)
            this.errorQueue.unshift(...errorsToReport);
            console.error('Failed to report errors:', e);
        } finally {
            this.isReporting = false;
        }
    }

    private async sendErrorReport(errors: ErrorReport[]) {
        // Implement your error reporting logic here
        // Examples: Sentry, LogRocket, custom API endpoint, etc.

        if (process.env.NODE_ENV === 'development') {
            // In development, just log to console
            console.group('Error Report (Development)');
            errors.forEach(error => {
                console.log('Error ID:', error.errorId);
                console.log('Message:', error.message);
                console.log('Stack:', error.stack);
                console.log('URL:', error.url);
                console.log('---');
            });
            console.groupEnd();
            return;
        }
    }

    // Public method to manually report errors
    reportError(error: Error, context?: any): string {
        return this.handleError(error, 'manual');
    }

    // Get current error queue (for debugging)
    getErrorQueue(): ErrorReport[] {
        return [...this.errorQueue];
    }

    // Clear error queue
    clearErrorQueue() {
        this.errorQueue = [];
    }
}

// Export singleton instance
export const globalErrorHandler = GlobalErrorHandler.getInstance();

// Export convenience functions
export const reportError = (error: Error, context?: any) =>
    globalErrorHandler.reportError(error, context);

export const handleError = (error: Error, source?: string) =>
    globalErrorHandler.handleError(error, source);

export default globalErrorHandler;
